<script setup>
import { ref, computed, onMounted, onUnmounted, inject, watch } from 'vue'
import BaseCom from './BaseCom.vue'
import eventBus from '../utils/eventBus'

// 屏幕宽度响应式变量
const screenWidth = ref(window.innerWidth)

// 更新屏幕宽度的函数
const updateScreenWidth = () => {
  screenWidth.value = window.innerWidth
}

// 定义组件属性
const props = defineProps({
  animation: {
    type: Object,
    default: () => ({
      enabled: true,
      duration: '1s',
      delay: '0ms',
      easing: 'ease-out'
    })
  },
  contentId: {
    type: String,
    default: ''
  },
  // 新增参数，控制上浮动画是否开启
  floatAnimationEnabled: {
    type: Boolean,
    default: true
  },
  // fontScale: {
  //   type: Number,
  //   default: 1
  // }
})

// 定义emits
const emit = defineEmits(['edit'])

// 处理编辑事件
const handleEdit = () => {
  // 发布编辑事件到EventBus
  eventBus.emit('text-edit-request', {
    config: config.value,
    contentId: props.contentId || `text-${Date.now()}`,
    type: comType
  });
  
  // 保留原有逻辑作为备用
  if (handleTextComEdit) {
    handleTextComEdit(props.contentId)
  } else {
    // 备用方案：发出edit事件
    emit('edit', { data: config.value, type: comType, id: props.contentId })
  }
}

// 给BaseCom组件添加ref引用
const baseComRef = ref(null)
const comType = 'text'
// 处理BaseCom组件的鼠标移入事件
const handleBaseComMouseEnter = () => {
  // 当TextCom的BaseCom组件鼠标移入时，通知父级ContainerCom的BaseCom实例
  if (baseComRef.value) {
    const parentBaseCom = baseComRef.value.$parent?.$parent
    if (parentBaseCom?.hideEditButton) {
      parentBaseCom.hideEditButton()
    }
  }
}
const defaultVal = {
  zh: '',
  en: '',
  fontSize: '16px',
  color: '#333333',
  fontWeight: 'normal'
}
const contentMap = inject('contentMap', {})
const isLoggedIn = inject('isLoggedIn', false)
const currentLanguage = inject('currentLanguage', 'zh')
const handleTextComEdit = inject('handleTextComEdit', null)
// 动画相关状态和引用
const isVisible = ref(false)
const textRef = ref(null)
const config = ref(defaultVal)
// const editable = ref(false)
let observer = null
// 添加watch监听器，监听contentId变化
watch(contentMap, (newValue) => {
  config.value = newValue[props.contentId] || defaultVal
}, { immediate: true, deep: true })

// 订阅文本更新事件
const unsubscribeTextUpdate = eventBus.on('text-updated', (data) => {
  // 只有当更新的数据与当前组件ID匹配时才更新
  if (data.contentId === props.contentId) {
    config.value = { ...config.value, ...data.config }
    console.log(`TextCom ${props.contentId} updated via EventBus`)
  }
})

// 组件卸载时取消订阅
onUnmounted(() => {
  unsubscribeTextUpdate()
})

// 计算文本基础样式
const baseStyle = computed(() => {
  // 解析fontSize，提取数值和单位
  const fontSizeMatch = config.value.fontSize.match(/^(\d+\.?\d*)([a-zA-Z%]+)$/)
  let border = {}
  if (config.value.border) {
    border = {
      border: '1px solid hsla(0, 0%, 100%, .4)',
      paddingLeft: '15px',
      paddingRight: '15px',
      borderRadius: '20px'
    }
  }
  if (!fontSizeMatch) {
    // 如果解析失败，返回原始fontSize
    return {
      fontSize: config.value.fontSize,
      color: config.value.color,
      fontWeight: config.value.fontWeight,
      display: 'inline-block',
      zIndex: 1,
      position: 'relative',
      whiteSpace: 'pre-wrap', // 确保换行符正确显示
      ...border
    }
  }
  
  const fontSizeValue = parseFloat(fontSizeMatch[1])
  const fontSizeUnit = fontSizeMatch[2]
  
  // 根据屏幕宽度计算系数,最小0.5,最大1.5
  let scaleFactor =  screenWidth.value / 1600
  scaleFactor = Math.max(0.7, Math.min(1.3, scaleFactor))
  // 计算新的fontSize
  const newFontSize = fontSizeValue * scaleFactor
  
  return {
    fontSize: `${newFontSize}${fontSizeUnit}`,
    color: config.value.color,
    fontWeight: config.value.fontWeight,
    border: config.value.border? '1px solid hsla(0, 0%, 100%, .4)': 'none',
    display: 'inline-block',
    zIndex: 1,
    position: 'relative',
    whiteSpace: 'pre-wrap', // 确保换行符正确显示
    ...border
  }
})

// 计算动画样式
const animationClass = computed(() => {
  return {
    'float-in': props.animation.enabled && props.floatAnimationEnabled
  }
})

// 计算动画样式对象
// const animationStyle = computed(() => {
//   const style = {}
  
//   if (props.animation.enabled && props.floatAnimationEnabled) {
//     // 启用动画时，默认位置是 translateY(20px)
//     style.transform = 'translateY(20px)'
//     // 使用CSS变量传递动画参数给transition使用
//     style['--animation-duration'] = props.animation.duration
//     style['--animation-delay'] = props.animation.delay
//     style['--animation-easing'] = props.animation.easing
//   } else {
//     // 禁用动画时，默认位置是 translateY(0)
//     style.transform = 'translateY(0)'
//   }
  
//   return style
// })

// 处理交叉观察器回调
const handleIntersect = (entries) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
          // 当元素可见度达到20%时，添加可见类
          entry.target.classList.add("visible");
          entry.target.classList.remove("invisible");
          // 移除unobserve调用，允许元素每次进入视口都触发动画
        } else {
          // 当元素完全离开视口时，重置为不可见状态，允许再次进入时触发动画
          // 添加防抖机制，只有当元素完全离开视口时才重置
          if (entry.intersectionRatio <= 0) {
            entry.target.classList.add("invisible");
            entry.target.classList.remove("visible");
          }
        }
  })
}

// 生命周期钩子
onMounted(() => {
  // 添加窗口大小变化监听
  window.addEventListener('resize', updateScreenWidth)
  
  // 延迟创建观察器，确保元素已渲染
  setTimeout(() => {
    // 只有当animation.enabled和floatAnimationEnabled都为true时才创建观察器
    if (props.animation.enabled && props.floatAnimationEnabled && textRef.value) {
      // 创建交叉观察器，保持持续观察
      observer = new IntersectionObserver(handleIntersect, {
        // root: null,
        // rootMargin: '0px',
        // threshold: 0.5
      })
      
      // 开始观察元素（不再取消观察，保持持续监听）
      observer.observe(textRef.value)
      
      // 初始检查：如果元素已经在视窗内，立即触发动画
      const rect = textRef.value.getBoundingClientRect()
      const isInViewport = rect.top < window.innerHeight && rect.bottom >= 0
      if (isInViewport) {
        isVisible.value = true
      }
    } else {
      // 如果动画未启用，则直接显示文本，避免不显示内容
      isVisible.value = true
    }
  }, 100)
})

onUnmounted(() => {
  // 移除窗口大小变化监听
  window.removeEventListener('resize', updateScreenWidth)
  
  if (observer && textRef.value) {
    observer.unobserve(textRef.value)
    observer.disconnect()
  }
})
</script>

<template>
  <!-- 直接使用BaseCom组件，在其插槽中渲染文本内容 -->
  <BaseCom
    ref="baseComRef"
    :editable="isLoggedIn"
    :contentId="contentId"
    @edit="handleEdit"
    @mouseenter="handleBaseComMouseEnter"
  >
    <!-- 在BaseCom的插槽中直接渲染文本，应用样式和引用 -->
    <!-- 使用config中的content，或者slot -->
    <div 
      v-if="config[currentLanguage]"
      :style="[baseStyle]"
      :class="animationClass"
      class="text-content"
      ref="textRef"
    >
      {{ config[currentLanguage] }}
    </div>
    <!-- <div 
      v-else
      :style="[baseStyle, animationStyle]"
      :class="animationClass"
      ref="textRef"
    >
      <slot></slot>
    </div> -->
  </BaseCom>
</template>

<style scoped>
/* 确保文本内容正确显示 */
:deep(.content-area) {
  min-height: auto;
  display: inline-block;
  position: relative;
  z-index: 1;
  min-width: 40px;
  /* min-height: 50px; */
}
:deep(.mask-overlay) {
  padding: 0 4px;
  align-items: center;
}

/* 确保遮罩层显示在最上层 */
:deep(.hover-mask-container) {
  min-height: 50px; /* 确保有足够的高度显示内容 */
  position: relative;
  z-index: 10; /* 提高遮罩层的z-index */
}

/* 定义从下到上浮动的动画 */
.float-in {
  /* animation-name: floatIn;
  animation-fill-mode: both;
  animation-play-state: running; */
  transition: all 2s cubic-bezier(0.22, 1, 0.36, 1);
}
.float-in.invisible {
  transform: translateY(80px);
  opacity: 0;
}

.float-in.visible {
  transform: translateY(0);
  opacity: 1;
}

@keyframes floatIn {
  from {
    transform: translateY(20px);
  }
  to {
    transform: translateY(0);
  }
}

/* 确保动画正常工作 */
:deep(.content-area) > div {
  display: inline-block;
  opacity: 1; /* 默认显示，避免初始透明导致不显示 */
  position: relative;
  z-index: 1;
}

/* 优化动画触发机制 */
:deep(.content-area) > div.float-in {
  /* 直接使用动画，而不是transition */
  /* animation-name: floatIn;
  animation-duration: var(--animation-duration, 1s);
  animation-timing-function: var(--animation-easing, ease-out);
  animation-delay: var(--animation-delay, 0ms);
  animation-fill-mode: both;
  animation-play-state: running; */
}

/* 额外确保遮罩层在最上层 */
:deep(.hover-mask) {
  position: absolute;
  z-index: 100; /* 进一步提高遮罩层的z-index */
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
}
.text-content {
  cursor: default ;
}
</style>